import React, { useEffect, useState } from "react";
import api, { formatRupiah, formatTanggal, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { statusBadgeClass } from "@/lib/surat-status";
import { PageHeader } from "@/components/PageHeader";
import StatsPanel from "@/components/StatsPanel";
import {
  Layers,
  ReceiptText,
  PlusCircle,
  ArrowUpRight,
  LayoutDashboard,
  User,
} from "lucide-react";

export default function DashboardHome() {
  const { user } = useAuth();
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/surat");
        setRecent(data.slice(0, 6));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-10" data-testid="dashboard-home">
      <PageHeader
        overline="Dashboard"
        title={`Halo, ${user?.name?.split(" ")[0] || user?.username}.`}
        description={
          isAdmin
            ? "Ringkasan seluruh aktivitas penerbitan surat di sistem."
            : "Ringkasan penerbitan surat di seluruh sistem + data pribadi Anda."
        }
        icon={LayoutDashboard}
        tone="brand"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="bg-white" data-testid="quick-view-rekap">
              <Link to="/surat">Lihat Rekap <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild className="bg-brand text-white hover:bg-brand-600" data-testid="quick-create-surat">
              <Link to="/surat/baru"><PlusCircle className="mr-2 h-4 w-4" /> Buat Surat</Link>
            </Button>
          </div>
        }
      />

      {/* SYSTEM-WIDE STATS (admin & user) */}
      <StatsPanel
        scope="all"
        title="Statistik Sistem"
        subtitle={isAdmin ? "Mencakup semua user pembuat surat." : "Seluruh aktivitas di sistem (lihat-saja)."}
        testIdPrefix="stats-all"
        accentTone="brand"
      />

      {/* PERSONAL STATS (user role only) */}
      {!isAdmin && (
        <div className="relative">
          <div className="mb-1 flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center rounded-md bg-gradient-to-br from-teal-500 to-teal-700 text-white shadow-sm">
              <User className="h-4 w-4" />
            </div>
            <h2 className="font-heading text-lg font-semibold tracking-tight text-ink-900">
              Data Pribadi Saya
            </h2>
          </div>
          <p className="mb-4 text-xs text-ink-500">
            Hanya menampilkan surat yang Anda terbitkan sendiri.
          </p>
          <StatsPanel
            scope="mine"
            title="Statistik Pribadi"
            subtitle="SPH & Invoice yang Anda buat."
            testIdPrefix="stats-mine"
            accentTone="invoice"
          />
        </div>
      )}

      {/* RECENT */}
      <section>
        <div className="mb-4 flex items-end justify-between">
          <div>
            <p className="overline">Aktivitas terbaru</p>
            <h2 className="mt-1 font-heading text-2xl font-semibold tracking-tight">Surat terbaru</h2>
          </div>
          <Link to="/surat" className="text-sm font-medium text-brand hover:underline">
            Semua surat →
          </Link>
        </div>

        <Card className="overflow-hidden border-border shadow-none">
          <div className="divide-y divide-border">
            {recent.length === 0 && (
              <div className="p-10 text-center text-sm text-ink-500">
                Belum ada surat. Mulai dengan menekan tombol "Buat Surat".
              </div>
            )}
            {recent.map((s) => {
              const isSPH = s.jenis === "SPH";
              return (
                <div
                  key={s.id}
                  className="flex flex-col gap-2 p-5 transition-colors hover:bg-secondary/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <div className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md ${isSPH ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                      {isSPH ? <Layers className="h-4 w-4" /> : <ReceiptText className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-ink-900">{s.nomor}</span>
                        <Badge className={isSPH ? "bg-sky-100 text-sky-800 hover:bg-sky-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>
                          {s.jenis}
                        </Badge>
                      </div>
                      <p className="mt-1 truncate text-sm text-ink-700">
                        {s.nama_pemohon} · {s.kategori}
                      </p>
                      <p className="mt-1 text-xs text-ink-500">
                        {formatTanggal(s.tanggal)} · oleh @{s.user_username}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 pl-12 sm:pl-0">
                    <div className="font-mono text-sm font-medium text-ink-900">{formatRupiah(s.nominal)}</div>
                    <Badge className={statusBadgeClass(s.status)}>{s.status}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </section>
    </div>
  );
}
